import React, { useState, useEffect } from 'react';
import './EmailAttachments.css';

const EmailAttachments = ({ emailId, token }) => {
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (emailId && token) {
            fetchAttachments();
        }
    }, [emailId, token]);

    const fetchAttachments = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/emails/${emailId}/attachments`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setAttachments(data.attachments || []);
            } else {
                throw new Error('Erreur lors du chargement des pièces jointes');
            }
        } catch (err) {
            console.error('Erreur:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const downloadAttachment = async (attachmentId, filename, pdfVersion = false) => {
        try {
            const url = `/api/emails/attachments/${attachmentId}/download${pdfVersion ? '?pdf_version=true' : ''}`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = pdfVersion ? `${filename.split('.')[0]}.pdf` : filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
            } else {
                throw new Error('Erreur lors du téléchargement');
            }
        } catch (err) {
            console.error('Erreur téléchargement:', err);
            alert('Erreur lors du téléchargement: ' + err.message);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (mimeType) => {
        if (mimeType.startsWith('image/')) return '🖼️';
        if (mimeType.includes('pdf')) return '📄';
        if (mimeType.includes('word')) return '📝';
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
        if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
        if (mimeType.includes('zip') || mimeType.includes('rar')) return '🗜️';
        return '📎';
    };

    if (loading) {
        return (
            <div className="attachments-container">
                <div className="attachments-header">
                    <h4>📎 Pièces jointes</h4>
                </div>
                <div className="attachments-loading">Chargement...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="attachments-container">
                <div className="attachments-header">
                    <h4>📎 Pièces jointes</h4>
                </div>
                <div className="attachments-error">Erreur: {error}</div>
            </div>
        );
    }

    if (attachments.length === 0) {
        return null; // Ne pas afficher le composant s'il n'y a pas de pièces jointes
    }

    return (
        <div className="attachments-container">
            <div className="attachments-header">
                <h4>📎 Pièces jointes ({attachments.length})</h4>
            </div>
            <div className="attachments-list">
                {attachments.map((attachment) => (
                    <div key={attachment.id} className="attachment-item">
                        <div className="attachment-info">
                            <span className="attachment-icon">
                                {getFileIcon(attachment.mime_type)}
                            </span>
                            <div className="attachment-details">
                                <div className="attachment-name" title={attachment.original_filename}>
                                    {attachment.filename}
                                </div>
                                <div className="attachment-meta">
                                    {formatFileSize(attachment.file_size)} • {attachment.mime_type}
                                </div>
                            </div>
                        </div>
                        <div className="attachment-actions">
                            <button
                                className="download-btn"
                                onClick={() => downloadAttachment(attachment.id, attachment.filename)}
                                title="Télécharger le fichier original"
                            >
                                ⬇️ Original
                            </button>
                            {attachment.has_pdf_version && (
                                <button
                                    className="download-btn pdf-btn"
                                    onClick={() => downloadAttachment(attachment.id, attachment.filename, true)}
                                    title="Télécharger la version PDF"
                                >
                                    📄 PDF
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default EmailAttachments;